import type { BlockGuide } from "./types";

export const guide: BlockGuide = {
  slug: "reddit",
  app: "Reddit",
  title: "How to block Reddit on iPhone — the app with no bottom",
  h1: "How to block Reddit",
  description:
    "Screen Time steps, why Reddit's comment threads never resolve, and what to do about the tab you keep reopening in Safari after blocking the app.",
  targets: [
    "how to block reddit on iphone",
    "block reddit app",
    "stop browsing reddit",
    "reddit time waster",
  ],
  published: "2026-09-03",
  standfirst:
    "Reddit is the only one of these where the trap is not a feed. It is a conversation that never finishes, and you were promised an answer.",
  takeaways: [
    "Blocking the app does almost nothing on its own, because Reddit works fine in a browser and everybody who blocks the app rediscovers this within a day.",
    "The pull is a comment tree that never bottoms out — each thread promises a resolution that is always one reply deeper.",
    "Content restrictions on the website, not just an app limit, is the only version of this that holds.",
  ],
  mechanic: [
    "Every other app on this list is a feed you scroll. Reddit is a search result you fell into. The typical session does not begin with boredom; it begins with a genuine question — a camera, a symptom, a passage, a purchase — and Reddit is frequently the best answer on the internet, which is exactly what makes it dangerous.",
    "The trap is the comment tree. A thread presents itself as a discussion with a conclusion in it somewhere, and the structure guarantees there is always one more reply below the one that nearly settled it. Unlike a feed, which you leave when bored, a thread is left only by deciding the answer is not coming — which is a much harder decision to make and one the interface never prompts.",
    "Then the sidebar does the rest. Every thread is surrounded by other communities, each with the same shape, and the site's whole architecture is built around lateral movement between them. An hour on Reddit rarely feels wasted while it is happening, which is the difference between it and an hour of short video, and it is why people defend it right up until they look at the number.",
  ],
  limitFails:
    "An app limit on Reddit is the easiest one on this page to route around, because old.reddit.com and the mobile site work perfectly well in Safari and the habit moves there within about a day. The second problem is that Reddit sessions are frequently launched from a search engine rather than from the icon, so the limit is not even in the path — you were looking for an answer to something, and the app was never opened at all.",
  instead: [
    "Block the website as well as the app, using Content & Privacy Restrictions → Web Content → Limit Adult Websites, then add reddit.com to Never Allow.",
    "Log out everywhere. A logged-out Reddit has no subscriptions and no personalised front page, which removes most of the reason to arrive without a question.",
    "Keep the search use and lose the browsing: append the site to a search engine query when you genuinely need it, rather than opening the front page.",
    "If the pull is specifically one community, unsubscribe from it rather than blocking the site. Precision works better here than it does anywhere else on this list.",
  ],
  verses: [
    {
      verse: {
        text: "All things are full of weariness beyond uttering. The eye is not satisfied with seeing, nor the ear filled with hearing.",
        ref: "Ecclesiastes 1:8",
      },
      note: "Ecclesiastes is describing the exhaustion of consumption without conclusion, which is precisely the state a long thread leaves you in. The eye is not satisfied is not a complaint about appetite being wrong; it is an observation that satisfaction is not the sort of thing looking produces, no matter how much of it you do.",
    },
    {
      verse: {
        text: 'Jesus answered her, "Martha, Martha, you are anxious and troubled about many things, but one thing is needed."',
        ref: "Luke 10:41–42",
      },
      note: "Martha is not accused of doing anything wrong — she is doing something good, at length, while something better goes unattended. Many things is the diagnosis, and it is a remarkably apt one for an evening spent reading forty strangers being interesting about subjects that will never come up again.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The website is the app",
      id: "the-website-is-the-app",
    },
    {
      type: "p",
      text: "This is the one guide on the site where blocking the app alone is close to useless. Reddit's mobile website is fully functional, most people already have it in their history, and the redirect from a search result opens it without touching the icon.",
    },
    {
      type: "p",
      text: "So the version that works has to include Web Content restrictions. It is a longer setup and it is the only part of this page that changes anything.",
    },
    {
      type: "h2",
      text: "Threads do not end",
      id: "threads-do-not-end",
    },
    {
      type: "p",
      text: "A feed is left when you get bored. A thread is left when you decide the answer is not coming, and nothing in the interface ever suggests that moment has arrived — there is always another reply, and it might be the good one.",
    },
    {
      type: "note",
      text: "A rule that works surprisingly well: read the top comment and close the tab. It contains the answer roughly nine times in ten and you can test that claim tonight.",
    },
    {
      type: "h2",
      text: "It is not a waste and that is the problem",
      id: "it-is-not-a-waste-and-that-is-the-problem",
    },
    {
      type: "p",
      text: "Most people leave a short-video session feeling faintly ashamed. Very few leave Reddit feeling that way, because they learned things — and they did, and the hour is still gone, and the concentration around it is still broken.",
    },
    {
      type: "p",
      text: "That is what makes it hard to argue with yourself about, and why the useful question is not whether it was worthwhile but {what it cost}.",
      links: {
        "what it cost": "/verses/bible-verses-about-time",
      },
    },
  ],
  faq: [
    {
      q: "How do I block Reddit on my iPhone?",
      a: "Block the app through Screen Time and the website through Settings → Screen Time → Content & Privacy Restrictions → Web Content → Never Allow, adding reddit.com. Without the second step the habit moves to Safari inside a day.",
    },
    {
      q: "Why is Reddit harder to quit than other apps?",
      a: "Because most sessions start with a genuine question and a comment tree that never resolves. You are not scrolling out of boredom; you are waiting for an answer that is always one reply deeper.",
    },
    {
      q: "Can I keep using Reddit for research?",
      a: "Yes, and that is the sensible version: search for the specific thing, read the top comment, close the tab. What you are giving up is the front page, which is the part that has no question attached.",
    },
  ],
  relatedApps: ["twitter", "safari"],
  related: ["screen-time-limits-dont-work", "dopamine-detox-christian"],
  relatedPrayers: ["prayer-for-focus", "prayer-before-you-open-your-phone"],
};
